/**
 * Contract v1.26: `public_paths`, the app paths a signed-out visitor may open.
 *
 * A mounted app is normally behind sign-in: `AppMountedRoute` sends a
 * signed-out visitor to the app's `marketing_path` before the iframe mounts.
 * An app can list the parts of itself that are public (a deals feed, product
 * pages) so search engines and shared links reach them; there the host mounts
 * the iframe with an anonymous session (no `sub`).
 *
 * Grammar, per entry (validated on save by `validatePublicPaths` in
 * functions/src/routes/admin-api/v1/index.ts; re-checked here because the
 * manifest arrives as opaque JSON):
 *   - `/deals`      exact: `/deals` (and `/deals/`), nothing else
 *   - `/product/*`  the prefix and everything below it: `/product`,
 *                   `/product/123`, `/product/123/x`, but not `/products`
 *   - `/*`          every path in the app
 *   - `/`           the app's root only
 * Segments are letters, digits and `-` `_` `.` `~` (no `.`/`..` segment); no
 * other wildcard. At most 20 entries of at most 200 characters. The path being
 * checked has its query string and hash dropped first, and its dot segments
 * resolved the way the browser will resolve them when it loads the iframe.
 */

export const PUBLIC_PATHS_MAX = 20;
export const PUBLIC_PATH_MAX_LENGTH = 200;

const SEGMENT = /^[A-Za-z0-9_.~-]+$/;

/** True when `entry` is a well-formed `public_paths` pattern. */
export function isPublicPathPattern(entry: unknown): entry is string {
  if (typeof entry !== 'string' || entry.length === 0 || entry.length > PUBLIC_PATH_MAX_LENGTH) return false;
  if (!entry.startsWith('/')) return false;
  if (entry === '/' || entry === '/*') return true;
  const base = entry.endsWith('/*') ? entry.slice(0, -2) : entry;
  const segments = base.slice(1).split('/');
  return segments.every((s) => SEGMENT.test(s) && s !== '.' && s !== '..');
}

/** The declared patterns, or `[]` when the key is missing or malformed. */
export function publicPathsOf(manifest: Record<string, unknown> | null | undefined): string[] {
  const raw = manifest?.public_paths;
  if (!Array.isArray(raw) || raw.length > PUBLIC_PATHS_MAX) return [];
  return raw.filter(isPublicPathPattern);
}

/**
 * The pathname the browser will load for an app path: query and hash
 * dropped, dot segments (including `%2e%2e`) resolved, trailing slash
 * trimmed. Built on a fixed origin so a leading `//` stays a path.
 */
function normalizeAppPath(appPath: string): string | null {
  try {
    const raw = appPath.startsWith('/') ? appPath : `/${appPath}`;
    const pathname = new URL(`http://app.invalid${raw}`).pathname;
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : '/';
  } catch {
    return null;
  }
}

/** True when `appPath` (an app-relative path, may carry `?query#hash`) matches a pattern. */
export function matchesPublicPath(appPath: string, patterns: readonly string[]): boolean {
  if (patterns.length === 0) return false;
  const path = normalizeAppPath(appPath);
  if (path === null) return false;
  return patterns.some((pattern) => {
    if (!isPublicPathPattern(pattern)) return false;
    if (pattern.endsWith('/*')) {
      const base = pattern.slice(0, -2);
      return base === '' || path === base || path.startsWith(`${base}/`);
    }
    return path === pattern;
  });
}

/** Whether a signed-out visitor may open `appPath` of the app with this manifest. */
export function isPublicAppPath(
  appPath: string,
  manifest: Record<string, unknown> | null | undefined,
): boolean {
  return matchesPublicPath(appPath, publicPathsOf(manifest));
}
